import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { api } from '@/lib/api';
import { trackEvent } from '@/lib/faro';
import { formatDate } from '@/lib/utils';
import { useToast } from '@/components/ui/toast';
import type { Notification, PaginatedResponse, OwnershipRequest } from '@/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, Check, Crown, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

export default function NotificationsPage() {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const { toast } = useToast();
    const [denialReason, setDenialReason] = useState<Record<number, string>>({});
    const [showRead, setShowRead] = useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ['notifications'],
        queryFn: () => api.get<PaginatedResponse<Notification>>('/notifications'),
    });

    const canActAsDom = user?.role === 'dom' || user?.role === 'switch' || user?.role === 'admin';

    const { data: pendingOwnership } = useQuery({
        queryKey: ['ownership-pending'],
        queryFn: () => api.get<{ data: OwnershipRequest[] }>('/ownership/pending'),
        enabled: canActAsDom,
    });

    const markRead = useMutation({
        mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    });

    const markAllRead = useMutation({
        mutationFn: () => api.post('/notifications/read-all'),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    });

    const respondOwnership = useMutation({
        mutationFn: ({ id, action, denial_reason }: { id: number; action: 'approve' | 'deny'; denial_reason?: string }) =>
            api.post(`/ownership/${id}/respond`, { action, denial_reason }),
        onSuccess: (_, vars) => {
            trackEvent('ownership_responded', { action: vars.action });
            toast(vars.action === 'approve' ? 'Ownership approved!' : 'Request denied.', 'success');
            queryClient.invalidateQueries({ queryKey: ['ownership-pending'] });
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
        onError: (err: Error) => toast(err.message),
    });

    const allNotifications = data?.data ?? [];
    const unreadCount = allNotifications.filter((n) => !n.read_at).length;
    const readCount = allNotifications.filter((n) => n.read_at).length;
    const notifications = showRead ? allNotifications : allNotifications.filter((n) => !n.read_at);
    const pendingRequests = pendingOwnership?.data ?? [];

    const getLabel = (n: Notification) => {
        const d = n.data;
        switch (d.type) {
            case 'payment_received':
                return `Payment of $${d.amount} received on "${d.contract_title}"`;
            case 'contract_assigned':
                return `${d.dom_name} assigned you a new contract: "${d.contract_title}"`;
            case 'contract_signed':
                return `${d.sub_name} signed "${d.contract_title}"`;
            case 'contract_closed':
                return `"${d.contract_title}" was ${d.closed_by === 'sub' ? 'abandoned' : 'closed'}`;
            case 'missed_payment':
                return `Missed payment on "${d.contract_title}" — $${d.penalty_amount} penalty`;
            case 'consent_requested':
                return `${d.requested_by} requested a ${d.change_type} change`;
            case 'consent_responded':
                return `Your consent request was ${d.status}`;
            case 'ownership_request':
                return `${d.sub_name} has requested you as their owner${d.message ? `: "${d.message}"` : ''}`;
            case 'ownership_response':
                return d.status === 'approved'
                    ? `${d.dom_name} approved your ownership request!`
                    : `${d.dom_name} denied your ownership request${d.denial_reason ? `: "${d.denial_reason}"` : ''}`;
            default:
                return 'New notification';
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">Notifications</h1>
                <div className="flex items-center gap-2">
                    {readCount > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowRead(!showRead)}
                            className="text-slate-400"
                        >
                            {showRead ? <EyeOff className="mr-1 h-3 w-3" /> : <Eye className="mr-1 h-3 w-3" />}
                            {showRead ? 'Hide read' : `Show read (${readCount})`}
                        </Button>
                    )}
                    {unreadCount > 0 && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => markAllRead.mutate()}
                        >
                            <Check className="mr-1 h-3 w-3" />
                            Mark all read
                        </Button>
                    )}
                </div>
            </div>

            {/* Pending Ownership Requests */}
            {pendingRequests.length > 0 && (
                <div className="space-y-2">
                    <h2 className="text-sm font-semibold text-amber-400 flex items-center gap-1.5">
                        <Crown className="h-4 w-4" />
                        Pending Ownership Requests
                    </h2>
                    {pendingRequests.map((req) => (
                        <Card key={req.id} className="border-amber-900/50">
                            <CardContent className="p-4 space-y-3">
                                <div className="flex items-center gap-3">
                                    {req.sub?.avatar_path ? (
                                        <img src={req.sub.avatar_path} alt="" className="h-8 w-8 rounded-full object-cover" />
                                    ) : (
                                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-900/50 text-xs font-bold text-violet-300">
                                            {req.sub?.display_name?.[0]?.toUpperCase() ?? '?'}
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <Link to={`/profiles/${req.sub_id}`} className="text-sm font-medium text-violet-400 hover:underline">
                                            {req.sub?.display_name}
                                        </Link>
                                        <span className="text-sm text-slate-400"> wants you as their owner</span>
                                        {req.message && (
                                            <p className="text-xs text-slate-500 mt-0.5 italic">"{req.message}"</p>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <input
                                        type="text"
                                        className="w-full rounded-md border border-slate-700 bg-slate-800/50 px-3 py-1.5 text-xs text-slate-200 placeholder:text-slate-500 focus:border-violet-500 focus:outline-none"
                                        placeholder="Denial reason (optional, only used if denied)..."
                                        value={denialReason[req.id] ?? ''}
                                        onChange={(e) => setDenialReason((p) => ({ ...p, [req.id]: e.target.value }))}
                                    />
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            className="bg-green-700 hover:bg-green-800"
                                            onClick={() => respondOwnership.mutate({ id: req.id, action: 'approve' })}
                                            disabled={respondOwnership.isPending}
                                        >
                                            Approve
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="border-red-700 text-red-400 hover:bg-red-900/20"
                                            onClick={() => respondOwnership.mutate({
                                                id: req.id,
                                                action: 'deny',
                                                denial_reason: denialReason[req.id] || undefined,
                                            })}
                                            disabled={respondOwnership.isPending}
                                        >
                                            Deny
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {isLoading ? (
                <p className="text-sm text-slate-400">Loading...</p>
            ) : notifications.length === 0 ? (
                <Card>
                    <CardContent className="py-8 text-center text-sm text-slate-400">
                        <Bell className="mx-auto mb-2 h-8 w-8 text-slate-600" />
                        {showRead ? 'No notifications yet.' : unreadCount === 0 && readCount > 0 ? 'No unread notifications.' : 'No notifications yet.'}
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-2">
                    {notifications.map((notification) => (
                        <Card
                            key={notification.id}
                            className={notification.read_at ? 'opacity-60' : ''}
                        >
                            <CardContent className="flex items-center justify-between p-3">
                                <div className="min-w-0">
                                    <p className="text-sm">{getLabel(notification)}</p>
                                    <p className="text-[10px] text-slate-500">
                                        {formatDate(notification.created_at)}
                                    </p>
                                </div>
                                {!notification.read_at && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 shrink-0"
                                        onClick={() => markRead.mutate(notification.id)}
                                    >
                                        <Check className="h-4 w-4" />
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
